import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { Loader2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Design system §6. Heights are fixed at 32 / 38 / 46 — nothing in between.
 *
 * - `accent` — one per view: the single most useful action (Choose a file, Export, Go Pro).
 * - `ink` — the neutral commitment button: nav CTA, "the app itself", the selected tool.
 * - `destructive` stays outlined until the confirm step, where it becomes `destructive-solid`.
 *
 * No shadows on buttons, ever.
 */
const buttonVariants = cva(
  // `cursor-pointer` is explicit: browsers default <button> to `cursor: default`, and
  // Tailwind's preflight does not change that.
  //
  // Disabled buttons deliberately get no cursor of their own. `pointer-events-none` takes them
  // out of hit-testing, so they inherit the plain arrow from the page and their hover styles
  // never fire. §6 asks for `cursor: not-allowed` here; we don't — see §11.
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-[9px] font-semibold whitespace-nowrap transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) outline-none focus-visible:ring-focus disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        accent:
          'bg-primary text-primary-foreground hover:bg-teal-600 disabled:bg-teal-200 disabled:text-primary-foreground data-[loading=true]:bg-teal-400 data-[loading=true]:hover:bg-teal-400',
        // Alias of `accent`. Other shadcn components call buttonVariants({ variant: "default" })
        // internally, so dropping this key silently unstyles anything added later by the CLI.
        default:
          'bg-primary text-primary-foreground hover:bg-teal-600 disabled:bg-teal-200 disabled:text-primary-foreground data-[loading=true]:bg-teal-400 data-[loading=true]:hover:bg-teal-400',
        ink: 'bg-ink text-surface-1 hover:bg-ink-raised disabled:bg-disabled',
        outline:
          'border border-border-strong bg-card text-foreground hover:border-disabled hover:bg-muted disabled:border-border disabled:text-disabled',
        ghost: 'text-ink-2 hover:bg-canvas hover:text-ink disabled:text-disabled',
        destructive:
          'border border-destructive-border bg-card text-destructive hover:bg-destructive-tint disabled:border-border disabled:text-disabled',
        'destructive-solid': 'bg-destructive text-destructive-foreground hover:bg-destructive-hover',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 rounded-md px-3.5 text-[13px]',
        md: 'h-[38px] rounded-lg px-4 text-sm',
        lg: 'h-[46px] rounded-xl px-6 text-base',
        icon: 'size-[38px] rounded-lg',
        'icon-sm': 'size-8 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'accent',
      size: 'md',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Swaps the label's leading icon for a 13px spinner and blocks interaction. */
    loading?: boolean;
  };

/**
 * `forwardRef` is load-bearing on React 18: a plain function component drops any ref it is
 * given. Radix hands its trigger ref to the `asChild` child, and popper-positioned primitives
 * (dropdown, popover, tooltip, select) use that node as the anchor to measure against — without
 * it they never position and render off-screen at `translate(0, -200%)`. Drop this and the
 * header's profile menu stops opening. Revisit only on React 19, where `ref` is a plain prop.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'accent', size = 'md', asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      disabled={disabled ?? loading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {/* Slottable marks which child is the element to slot onto, so the spinner can sit
          beside it. Without it, `asChild` sees two children — the spinner slot and the
          child — and Radix refuses to slot at all, even when the spinner is null. */}
      {loading ? <Loader2Icon className="size-[13px] animate-spin" /> : null}
      <Slot.Slottable>{children}</Slot.Slottable>
    </Comp>
  );
});

export { Button, buttonVariants };
export type { ButtonProps };
